import React from 'react';

import { Control, useWatch } from 'react-hook-form';
import { LbInput, LbSelect } from '@infoods/lifebase-ui';

import {
  ComboBox,
  InputBox,
  SelectSportPickers,
} from '../../../../shared/components/form-components';
import {
  CUSTOMER_SPORT_TYPE,
  customerSportTypeOptions,
} from '../../../constants/customerSportType';
import { transformToOptions } from '../../../../shared/utils/arrayUtils';
import {
  isDateLessMinAge,
  validateEmail,
} from '../../../../shared/utils/reactHookFormUtils';
import { useAdditionalInfoOptions } from '../../../hooks/useAdditionalInfoOptions';
import { CombinedCustomerInfo } from '../../../interfaces/Customer';

type AdditionalInfoFormProps = {
  control: Control<CombinedCustomerInfo>;
  entityId: number;
  defaultSchoolId?: number | null;
};

const AdditionalInfoForm = ({
  control,
  entityId,
  defaultSchoolId,
}: AdditionalInfoFormProps) => {
  const generalInputProps = {
    control,
    size: 'small',
    variant: 'standard',
  } as const;

  const privateInfoProps = { InputProps: { className: 'private-info' } };

  const [dateOfBirth, sportType] = useWatch({
    control,
    name: ['customer.dob', 'additionallyInfo.sportType'],
  });

  const { schools, sports } = useAdditionalInfoOptions(entityId, defaultSchoolId);

  return (
    <InputBox title="Additionally">
      <LbSelect
        {...generalInputProps}
        {...privateInfoProps}
        options={customerSportTypeOptions}
        name="additionallyInfo.sportType"
        label="Type"
        rules={{
          required: 'Type is required',
        }}
      />
      {sportType === CUSTOMER_SPORT_TYPE.ATHLETE_SCHOOL && (
        <>
          <ComboBox
            {...generalInputProps}
            {...privateInfoProps}
            name="additionallyInfo.schoolId"
            label="School/Team/Organization"
            rules={{ required: 'School/Team/Organization is required' }}
            options={transformToOptions({
              list: schools || [],
              valueKey: 'id',
              labelKey: 'name',
            })}
          />
          <LbInput
            {...generalInputProps}
            {...privateInfoProps}
            name="additionallyInfo.parentEmail"
            label="Parent/Guardian email"
            rules={{
              required: isDateLessMinAge(dateOfBirth, 18)
                ? false
                : 'Parent/Guardian email is required',
              validate: { validateEmail },
            }}
          />
          <SelectSportPickers
            {...privateInfoProps}
            control={control}
            sports={sports}
            sportFieldProps={{
              name: 'additionallyInfo.sport',
              rules: { required: 'Sport is required' },
            }}
            sportPositionFieldProps={{
              name: 'additionallyInfo.sportPosition',
              rules: { required: 'Position is required' },
            }}
          />
        </>
      )}
    </InputBox>
  );
};

export { AdditionalInfoForm };
